import {
  BinaryIcon,
  CodeIcon,
  FileUserIcon,
  HandHelpingIcon,
  HouseIcon,
  LineSquiggleIcon,
  ListTodoIcon,
  MessageSquareTextIcon,
  NotebookPenIcon,
  SettingsIcon,
  UserIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useRegen } from '@/contexts/regen-context'
import { useIndividuals } from '@/hooks/useIndividuals'
import { useServiceChanges } from '@/hooks/useServiceChanges'
import { useAppSelector } from '@/redux/hooks'

import { appConfig } from './app.config'

export const useNavigationConfig = () => {
  const { t } = useTranslation()
  const isDeveloper = useAppSelector(state => state.settings.isDeveloper)
  const { selectedIndividual, selectedIndividualId } = useIndividuals()

  // Not just "is there new data" — the dot must stay dark while documents are
  // still processing, matching the home banner. Otherwise it invites the user to
  // a page whose regen offer is deliberately hidden.
  const { offer } = useRegen()

  // Cleared by opening Hitta stöd — the `lastRun` diff alone has no read state,
  // so without the seen marker the dot stays lit until a later regen happens to
  // produce no change.
  const { hasUnseenChanges: hasServiceChanges } = useServiceChanges(selectedIndividualId)

  return {
    navMain: [
      {
        hideTitle: true,
        items: [
          {
            badge: offer.shouldOffer,
            icon: <HouseIcon />,
            title: t('navigation.home'),
            url: '/home',
          },
        ],
        title: '',
        url: '',
      },
      {
        hideTitle: false,
        items: [
          {
            badge: false,
            icon: <FileUserIcon />,
            title: t('navigation.documents'),
            url: '/documents',
          },
          {
            badge: false,
            icon: <NotebookPenIcon />,
            title: t('navigation.notes'),
            url: '/notes',
          },
          {
            badge: false,
            icon: <LineSquiggleIcon />,
            title: t('navigation.timeline'),
            url: '/timeline',
          },
          {
            badge: false,
            icon: <UserIcon />,
            title: t('navigation.individual', { name: selectedIndividual?.firstName ?? '' }),
            url: '/individual',
          },
        ],
        title: t('navigation.section.knowledge'),
        url: '',
      },
      {
        items: [
          {
            badge: hasServiceChanges,
            icon: <HandHelpingIcon />,
            title: t('navigation.services'),
            url: '/services',
          },
          {
            badge: false,
            icon: <ListTodoIcon />,
            title: t('navigation.errands'),
            url: '/errands',
          },
          {
            badge: false,
            icon: <MessageSquareTextIcon />,
            title: t('navigation.conversations'),
            url: '/conversations',
          },
        ],
        title: t('navigation.section.functions'),
        url: '',
      },
      {
        items: [
          {
            badge: false,
            icon: <SettingsIcon />,
            title: t('navigation.settings'),
            url: '/settings',
          },
        ],
        title: t('navigation.section.misc'),
        url: '',
      },
      ...(isDeveloper || appConfig.isLocalDeveloperMode
        ? [
            {
              items: [
                {
                  badge: false,
                  icon: <CodeIcon />,
                  title: 'Debug',
                  url: '/debug',
                },
              ],
              title: 'Developer Mode',
              url: '',
            },
          ]
        : []),
      ...(appConfig.isLocalDeveloperMode
        ? [
            {
              items: [
                {
                  badge: false,
                  icon: <BinaryIcon />,
                  title: 'Design System',
                  url: '/design-system',
                },
                {
                  badge: false,
                  icon: <BinaryIcon />,
                  title: 'AI Chat',
                  url: '/ai-chat',
                },
                {
                  badge: false,
                  icon: <BinaryIcon />,
                  title: 'Payload Admin',
                  url: '/admin',
                },
              ],
              title: 'Local Developer Mode',
              url: '',
            },
          ]
        : []),
    ],
  }
}
